import { inject, Injectable, Injector } from '@angular/core';
import { ApiRequestService } from '@core/auth/api-request.service';
import { config } from '@core/interfaces/api_baseurl';
import {
  BehaviorSubject,
  Observable,
  forkJoin,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { AccountDetails, Bank, BankDetails } from './connect-bank-types';
import { StorageService } from '@shared/storage.service';
import { SprintNextUtilService } from '@core/services/utils.service';
import { SharedService } from '@shared/shared.service';
import {
  ConfirmationModalConfig,
  ConfirmationModalService,
} from '@core/customCDK/cdkCustomMdl/services/confirmation-modal.service';
import { ConfirmationComponent } from '@core/customCDK/cdkCustomMdl/components/confirmation/confirmation.component';
import { Router } from '@angular/router';
import { CredentialsService } from '@page/user-profile/api-access-control/credentials/helper/credentials.service';

@Injectable({
  providedIn: 'root',
})
export class ConnectBankService {
  // selectedBank: Bank | null = null;
  get selectedBank() {
    return sessionStorage.getItem('selectedBank')
      ? JSON.parse(sessionStorage.getItem('selectedBank') as string)
      : null;
  }
  set selectedBank(v: Bank | null) {
    sessionStorage.setItem('selectedBank', JSON.stringify(v));
  }

  private _isBankModuleActive: BehaviorSubject<string> =
    new BehaviorSubject<string>('');

  get isBankModuleActive(): string {
    return this._isBankModuleActive.value;
  }
  set isBankModuleActive(v: string) {
    this._isBankModuleActive.next(v);
  }

  private _allBankDetailList: BehaviorSubject<BankDetails[] | []> =
    new BehaviorSubject<BankDetails[] | []>([]);
  private _selectedBankId: BehaviorSubject<number | ''> = new BehaviorSubject<
    number | ''
  >('');
  private _bankAllAccountList: BehaviorSubject<AccountDetails | null> =
    new BehaviorSubject<AccountDetails | null>(null);
  _filterBank: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  private _serviceTypeList: BehaviorSubject<any> = new BehaviorSubject<any>(
    null
  );

  topPenal: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  bankListData: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  get bankListData$() {
    return this.bankListData.asObservable();
  }
  get allBankDetailList(): BankDetails[] | [] {
    return this._allBankDetailList.getValue() as BankDetails[] | [];
  }

  get allBankDetailList$(): Observable<BankDetails[] | []> {
    return this._allBankDetailList.asObservable() as Observable<
      BankDetails[] | []
    >;
  }

  get bankAllAccountList(): AccountDetails | null {
    return this._bankAllAccountList.getValue() as AccountDetails | null;
  }

  get bankAllAccountList$(): Observable<AccountDetails | null> {
    return this._bankAllAccountList.asObservable() as Observable<AccountDetails | null>;
  }

  get bankId(): number | '' {
    return this._selectedBankId.value;
  }

  set bankId(v: number | '') {
    this._StorageService.setItem('bankId', v.toString());
    this._selectedBankId.next(v);
  }

  constructor(
    private _ApiRequestService: ApiRequestService,
    private _StorageService: StorageService,
    private _SharedService: SharedService,
    private _SprintNextUtilService: SprintNextUtilService,
    private _ConfirmationModalService: ConfirmationModalService,
    private Injector: Injector
  ) {
    let bankId = this._StorageService.getItem('bankId');
    // console.log(bankId);

    let allBankDetailList = this._StorageService.getItem('allBankDetailList');
    let bankAccountList = this._StorageService.getItem('bankAccountList');
    if (bankId) {
      this.bankId = +(bankId as string);
    }
    if (bankAccountList) {
      this._bankAllAccountList.next(bankAccountList);
    }
    if (allBankDetailList) {
      this._allBankDetailList.next(allBankDetailList);
    }
  }

  resetBankDetail() {
    this.bankId = '';
    this._bankAllAccountList.next(null);
    this._StorageService.removeItem('allBankDetailList');
    this._StorageService.removeItem('bankAccountList');
    this._StorageService.removeItem('bankId');
    // this._StorageService.removeItem('menuPanel');
  }

  getAllBankDetailsList(newFetch: boolean = false): Observable<any> {
    if (this.allBankDetailList.length > 0 && !newFetch)
      return of(this.allBankDetailList).pipe(
        tap(() => {
          this._allBankDetailList.next(this.allBankDetailList);
        })
      );
    return this._ApiRequestService
      .postData(config.verify_dashboard.dashboard_api)
      .pipe(
        tap((r: any) => {
          if (r.statuscode == 200) {
            if (r.data.length === 1) {
              this.bankId = (r.data[0] as BankDetails).bank.id;
            }
            let data = r.data;

            this._StorageService.setItem('allBankDetailList', data);
            this._allBankDetailList.next(r.data);
          }

          if (r.code == 201) {
            this.handleMissingCredentials(r.message);
            if (newFetch) {
              this._StorageService.removeItem('allBankDetailList');
              this._allBankDetailList.next([]);
            }
          }
        })
      );
  }

  private handleMissingCredentials(message: string): void {
    const customConfig: ConfirmationModalConfig = {
      data: {
        icon: 'info',
        msg: message,
        btnClose: 'Add Credentials',
        showConfirmButton: false,
        allowOutsideClick: false,
        allowEscapeKey: false,
        showCancelButton: false,
      },
    };

    this._ConfirmationModalService
      .init(ConfirmationComponent, customConfig)
      .subscribe((events: any) => {
        this.Injector.get(CredentialsService).GenerateAPIKey({
          showCloseIcon: false,
          
        });
        events.initRef.close();
        
      });
  }

  getBankList(): Observable<any> {
    return this._ApiRequestService.postData(config.createBank.getbanks);
  }

  getBankDtl(id: string): Observable<any> {
    const formData = new FormData();
    formData.append('bank_id', id);
    formData.append('type', 'details');
    return this._ApiRequestService.postData(
      config.createBank.getBankForm,
      formData
    );
  }

  getBankAllAccountList(
    id: string,
    newFetch: boolean = false
  ): Observable<any> {
    if (this.bankAllAccountList && this.bankId == id && !newFetch)
      return of(this.bankAllAccountList).pipe(
        tap(() => {
          this._bankAllAccountList.next(this.bankAllAccountList);
        })
      );
    const formData = new FormData();
    formData.append('bank_id', id);
    return this._ApiRequestService
      .postData(config.verify_dashboard.single_account, formData)
      .pipe(
        switchMap((r: any) => {
          if (r.statuscode == 200) {
            this._bankAllAccountList.next(r.data);
            this._StorageService.setItem(
              'bankAccountList',
              this.bankAllAccountList
            );
            this.bankId = +id;
          }
          return of(r.data ?? null);
        })
      );
  }

  getServiceTypeList(id: string): Observable<any> {
    const formData = new FormData();
    formData.append('bank_id', id);
    return this._ApiRequestService.postData(
      config.createBank.get_account_type,
      formData
    );
  }

  addBankAc(url: string, formData: any): Observable<any> {
    return this._ApiRequestService.postData(url, formData);
  }

  dynamicFork(id: string) {
    return forkJoin([this.getBankDtl(id), this.getServiceTypeList(id)]);
  }

  getAccountsList(obj: any) {
    let formdata = this._SprintNextUtilService.setFormData(obj);
    return this._ApiRequestService
      .postData(config.createBank.userAccountList, formdata)
      .pipe(
        switchMap((res: any) => {
          this._filterBank.next(res);
          return of(res);
        })
      );
  }

  getAccountDetails(formData: FormData): Observable<any> {
    return this._ApiRequestService.postData(
      config.createBank.getAccountDetails,
      formData
    );
  }

  getBankServiceTypeAndBankDtlFork(data: any[]) {
    return forkJoin([
      this.getAccountDetails(data[0]),
      this.getServiceTypeList(data[1]),
    ]);
  }

  getCrednitialForm(formData: FormData): Observable<any> {
    return this._ApiRequestService.postData(
      config.bankCrednitial.getFormdata,
      formData
    );
  }

  addCrednitial(formData: FormData): Observable<any> {
    return this._ApiRequestService.postData(
      config.bankCrednitial.addBankCrednitial,
      formData
    );
  }

  getCrednitial(formData: FormData): Observable<any> {
    return this._ApiRequestService.postData(
      config.bankCrednitial.getCrednitial,
      formData
    );
  }

  editKey(formData: FormData): Observable<any> {
    return this._ApiRequestService.postData(
      config.bankCrednitial.editCrednitial,
      formData
    );
  }

  topPanel(bankId: any) {
    const formdata = new FormData();
    //[{"key":"type","value":"services/settings\n","description":"","type":"default","enabled":true}]
    formdata.append('bank_id', bankId);
    formdata.append('type', 'services');
    return this._SharedService.topPanel(formdata).pipe(
      switchMap((res: any) => {
        console.log(res, 'res....');
        if (res.code == 200) {
          this.topPenal.next(res.data);
        }
        return of(res);
      })
    );
  }
  getPayoutNewReports(formdata: FormData) {
    return this._ApiRequestService.postData(
      config.reportsNew.payoutNew,
      formdata
    );
  }
  downloadFile(formdata: FormData) {
    return this._ApiRequestService.postData(
      config.reportsNew.download,
      formdata
    );
  }
  downloadWalletStatment(formdata: FormData) {
    return this._ApiRequestService.postData(
      config.walletStatmentReports.walletStatmentDownload,
      formdata
    );
  }
  getWalletTransaction(formdata: FormData) {
    return this._ApiRequestService.postData(
      config.walletStatmentReports.walletTransaction,
      formdata
    );
  }
  getEcollectReport(formdata: FormData) {
    return this._ApiRequestService.postData(
      config.ecollect.ecollectReport,
      formdata
    );
  }
  ecollectDownload(formdata: FormData) {
    return this._ApiRequestService.postData(
      config.ecollect.ecollectDownload,
      formdata
    );
  }
  bankList() {
    return this._ApiRequestService.postData(config.reportsNew.filter);
  }
  upiCollectionReport(formdata: FormData) {
    return this._ApiRequestService.postData(
      config.upiCollection.upiCollectionReport,
      formdata
    );
  }
  upiCollectionDownload(formdata: FormData) {
    return this._ApiRequestService.postData(
      config.upiCollection.upiCollectionExport,
      formdata
    );
  }
  getBeneficiaryReport(formdata: FormData) {
    return this._ApiRequestService.postData(
      config.beneficiary.beneficiaryReport,
      formdata
    );
  }
  downloadBenificaryReport(formdata: FormData) {
    return this._ApiRequestService.postData(
      config.beneficiary.beneficiaryExport,
      formdata
    );
  }
  getTxnIssues(formdata: FormData) {
    return this._ApiRequestService.postData(
      config.reportsNew.getTxnIssues,
      formdata
    );
  }
  getVpaReport(formdata: FormData) {
    return this._ApiRequestService.postData(
      config.vpacreation.getVpaReport,
      formdata
    );
  }
  exportVpaReport(formdata: FormData) {
    return this._ApiRequestService.postData(
      config.vpacreation.exportVpaReport,
      formdata
    );
  }
}
